/**
 * MobileAlertCard — a single alert row rendered to mimic the Android mobile app's
 * alert card (palcare_mobile_android cloud_4_0/alerts/AlertsScreen.kt).
 *
 * Interaction:
 *   - Hold to Accept (1.5s) when status is Waiting/Overdue & not yet accepted
 *   - Hold to Finish (1.5s) when status is Arrived, but ONLY if the session user
 *     is the one who accepted it (compare accepted_user_id to getUserId()).
 *   - No navigation on tap.
 *
 * Visual:
 *   - Colored full border + timer text:
 *       accepted (blue) > has ack (green) > elapsed >10min (red) > >5min (orange)
 *   - Device icon + area + near_area + elapsed duration on right
 *   - Footer action strip:
 *       "Alert Open: Hold to Accept"       (waiting, accept required)
 *       "Alert Cleared: Hold to Accept"    (cleared but not accepted)
 *       "Alert Accepted: Hold to Finish"   (arrived, finish required, I accepted)
 *       "Accepted by: <name>"              (accepted by someone else)
 *       "Cleared"                          (read-only)
 */

import { useEffect, useRef, useState } from 'react';
import '../../styles/mobile-alerts.css';
import PendantIcon from "@/pages/settings/palpendant-settings/components/PendantIcon.jsx";

const HOLD_MS = 1500;

/** 5 / 10-minute urgency thresholds match AlertsScreen.kt (ORANGE = 5min, RED = 10min). */
const ORANGE_MS = 5 * 60 * 1000;
const RED_MS = 10 * 60 * 1000;

const elapsedMs = (utcStr) => {
    if (!utcStr) return 0;
    const iso = utcStr.endsWith?.('Z') ? utcStr : `${utcStr}Z`;
    return Math.max(0, Date.now() - new Date(iso).getTime());
};

const formatElapsed = (ms) => {
    const total = Math.floor(ms / 1000);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    const pad = (n) => String(n).padStart(2, '0');
    if (h > 0) return `${h}:${pad(m)}:${pad(s)}`;
    return `${pad(m)}:${pad(s)}`;
};

const formatUtcToLocal = (utcStr) => {
    if (!utcStr) return '\u2014';
    try {
        const d = new Date(utcStr.endsWith?.('Z') ? utcStr : `${utcStr}Z`);
        return d.toLocaleString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
    } catch { return utcStr; }
};

const pickAccentColor = (row) => {
    const hasAck = Boolean(row.ack_datetime_utc);
    const accepted = Boolean(row.accepted_datetime_utc) || Boolean(row.accepted_display);
    if (accepted && !hasAck) return 'var(--color-primary, #0081C7)';
    if (hasAck) return 'var(--color-success, #0cce6b)';
    const ms = elapsedMs(row.event_datetime_utc);
    if (ms >= RED_MS) return 'var(--color-danger, #e63757)';
    if (ms >= ORANGE_MS) return 'var(--color-warning-text, #d97706)';
    return 'var(--color-text-muted, #6b7280)';
};

const DeviceIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
        <rect x="5" y="2" width="14" height="20" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
        <circle cx="12" cy="18.5" r="1.2" fill="currentColor" />
    </svg>
);

const WarningTriangleIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path fillRule="evenodd" clipRule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" />
    </svg>
);

const MobileAlertCard = ({
    row,
    sessionUserId,
    sessionUsername,
    onAccept,
    onFinish,
    onAcknowledge,
    onViewVideo,
    pending = false,
    highPriority = false,
    ackIntegrationActive = false,
    canViewVideo = false,
}) => {
    const [holdMs, setHoldMs] = useState(0);
    const [holding, setHolding] = useState(false);
    const [expanded, setExpanded] = useState(false);
    // Local "acknowledge in flight" flag. accept/finish/acknowledge all share the
    // page-level pendingEventId, so without this the hold-footer would flash
    // "Accepting…/Finishing…" during an acknowledge POST. Scopes that text to real
    // hold actions and drives the Acknowledge button's own label.
    const [acknowledging, setAcknowledging] = useState(false);
    /** Single source of truth for the hold lifecycle. Prevents ghost RAFs,
     *  re-entrant fires, and the "fired but never reset" state that blocks
     *  subsequent holds when `holdable` flips false mid-gesture. */
    const holdRef = useRef({ active: false, fired: false, startedAt: 0 });
    const rafRef = useRef(0);
    /** Set true when a hold completes and fires accept/finish. The body's
     *  click handler reads this to skip the expand toggle on the same gesture. */
    const firedThisGestureRef = useRef(false);
    const [, setTick] = useState(0);

    // 1s ticker so the elapsed label updates
    useEffect(() => {
        const t = setInterval(() => setTick((n) => n + 1), 1000);
        return () => clearInterval(t);
    }, []);

    // Clear the acknowledge flag once the in-flight request settles.
    useEffect(() => {
        if (!pending) setAcknowledging(false);
    }, [pending]);

    const accepted = Boolean(row.accepted_datetime_utc) || Boolean(row.accepted_display);
    const hasAck = Boolean(row.ack_datetime_utc);
    const canAccept = Boolean(onAccept)
        && Boolean(row.accept_required)
        && !accepted
        && (row.status === 'Waiting' || row.status === 'Overdue' || hasAck);
    const acceptedByMe = (
        (row.accepted_user_id != null && sessionUserId != null && String(row.accepted_user_id) === String(sessionUserId))
        || (row.accepted_username && sessionUsername && String(row.accepted_username) === String(sessionUsername))
    );
    const canFinish = Boolean(onFinish)
        && Boolean(row.finish_required)
        && row.status === 'Arrived'
        && acceptedByMe;

    // Acknowledge (POST) for event_type_id 40 alerts carrying an acknowledge_link,
    // still pre-clear. When the alert required acceptance it is the accepter's to
    // acknowledge — this used to accept any accepted alert, which let a second
    // caregiver acknowledge someone else's. Mirrors the desktop table.
    // "View Video" (the GET link) is offered to role_weight > 10 either way.
    const isType40 = (row.event_type_id ?? '').toString() === '40';
    const ackEligible = ackIntegrationActive && isType40 && Boolean(row.acknowledge_link) && !hasAck;
    const canAcknowledge = Boolean(row.accept_required) ? acceptedByMe : true;
    const showAcknowledge = ackEligible && canAcknowledge;
    const showViewVideo = ackEligible && canViewVideo;

    const action = canFinish ? 'finish' : canAccept ? 'accept' : null;

    const stopHold = () => {
        holdRef.current.active = false;
        holdRef.current.fired = false;
        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = 0;
        }
        setHolding(false);
        setHoldMs(0);
    };

    const step = () => {
        // If stopHold ran between frames, bail so we don't schedule a ghost loop.
        if (!holdRef.current.active) {
            rafRef.current = 0;
            return;
        }
        const delta = Date.now() - holdRef.current.startedAt;
        setHoldMs(delta);
        if (delta >= HOLD_MS && !holdRef.current.fired) {
            holdRef.current.fired = true;
            holdRef.current.active = false;
            rafRef.current = 0;
            // Suppress the click that follows pointerup so a successful hold
            // doesn't also toggle the expand state.
            firedThisGestureRef.current = true;
            setHolding(false);
            setHoldMs(0);
            if (action === 'accept') onAccept?.(row);
            else if (action === 'finish') onFinish?.(row);
            return;
        }
        rafRef.current = requestAnimationFrame(step);
    };

    const startHold = () => {
        // Reset gesture-fire flag at the very start of every press so the next
        // click can toggle expand even if a prior gesture fired the action.
        firedThisGestureRef.current = false;
        if (!action || pending) return;
        // Unconditionally reset — if a prior hold fired and the pointerup
        // handler didn't run (because holdable flipped), we still want a
        // fresh press on a newly-holdable card to animate.
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        holdRef.current = { active: true, fired: false, startedAt: Date.now() };
        setHoldMs(0);
        setHolding(true);
        rafRef.current = requestAnimationFrame(step);
    };

    const handleBodyClick = () => {
        if (firedThisGestureRef.current) {
            firedThisGestureRef.current = false;
            return;
        }
        setExpanded((v) => !v);
    };

    // While holding, listen on the window for pointerup / pointercancel so the
    // gesture always gets cleaned up — regardless of where the pointer ends up.
    // Relying on the card's own pointerup was fragile: if the pointer lifted
    // outside the card (after capture quirks, focus changes, etc.), stopHold
    // never ran and the next press's setState calls wouldn't trigger renders.
    useEffect(() => {
        if (!holding) return undefined;
        const handleUp = () => stopHold();
        window.addEventListener('pointerup', handleUp);
        window.addEventListener('pointercancel', handleUp);
        return () => {
            window.removeEventListener('pointerup', handleUp);
            window.removeEventListener('pointercancel', handleUp);
        };
    }, [holding]);

    useEffect(() => () => {
        holdRef.current.active = false;
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
    }, []);

    const accentColor = pickAccentColor(row);
    const elapsedLabel = formatElapsed(elapsedMs(row.event_datetime_utc));
    const progressPct = holding ? Math.min(100, (holdMs / HOLD_MS) * 100) : 0;

    // Title prefers the wander destination (near_area_name) when GDMS has
    // resolved one — but not while it's still "calculating", which would just
    // be noise for the caregiver. Compare lowercased so capitalization in the
    // payload doesn't matter.
    const hasUsableNearArea = row.near_area_name
        && !row.near_area_name.toLowerCase().startsWith('calculating');
    const areaLine = hasUsableNearArea
        ? row.near_area_name
        : (row.area_name || '—');
    // Subline shows the resident when one is associated. Otherwise (area-
    // device alerts, unassigned devices, etc. — cases where near_area_name
    // is also typically empty) we fall back to identifying the device, e.g.
    // "Waterproof Pendant #2486165". Final fallbacks keep something visible
    // if the device record is sparse.
    const deviceIdentity = row.device_name
        ? `${row.device_name}${row.manufacture_uuid ? ` #${row.manufacture_uuid}` : ''}`
        : '';
    const residentNames = (row.residents || []).map((r) => r.resident_name).filter(Boolean).join(', ');
    const subLine = residentNames
        || deviceIdentity
        || row.event_type
        || row.device_description
        || '';

    const footerText = (() => {
        if (pending && !acknowledging) return action === 'finish' ? 'Finishing…' : 'Accepting…';
        if (canFinish) return 'Alert Accepted: Hold to Finish';
        if (canAccept) return hasAck ? 'Alert Cleared: Hold to Accept' : 'Alert Open: Hold to Accept';
        if (accepted) return `Accepted by: ${row.accepted_display || row.accepted_username || '—'}`;
        if (hasAck) return 'Cleared';
        return '';
    })();

    const holdable = Boolean(action) && !pending;

    return (
        <div
            className={`mac-card${holdable ? ' mac-card--holdable' : ''}${holding ? ' mac-card--holding' : ''}`}
            style={{ borderColor: accentColor }}
            role={holdable ? 'button' : undefined}
            tabIndex={holdable ? 0 : undefined}
            onPointerDown={startHold}
        >
            {highPriority ? (
                <div className="mac-banner">
                    <span className="mac-banner__icon"><WarningTriangleIcon /></span>
                    <span className="mac-banner__text">
                        High Priority{row.event_type ? ` — ${row.event_type}` : ''}
                    </span>
                </div>
            ) : null}
            <div
                className="mac-body"
                onClick={handleBodyClick}
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
            >
                <div className="mac-icon">
                    {(row.manufacture_id === 1 && [2,3,4,5,6,16,19,49,55].includes(parseInt(row.device_type_id)) || row.manufacture_id === 4 && [1].includes(row.device_type_id))
                        ?   <PendantIcon width={"70%"} />
                        :   <DeviceIcon />
                    }
                </div>
                <div className="mac-text">
                    <div className="mac-title">{areaLine}</div>
                    {subLine ? <div className="mac-sub">{subLine}</div> : null}
                </div>
                <div className="mac-time" style={{ color: accentColor }}>{elapsedLabel}</div>
                <span className={`mac-chevron${expanded ? ' mac-chevron--open' : ''}`} aria-hidden="true">
                    {'\u25BE'}
                </span>
            </div>

            {expanded ? (
                <div className="mac-details">
                    {(row.manufacture_id === 1 && [2,3,4,5,6,16,19,49,55].includes(row.device_type_id) || row.manufacture_id === 4 && [1].includes(row.device_type_id)) ?
                        <>
                            <div className="mac-details__row">
                                <span className="mac-details__label">Alert Location</span>
                                <span className="mac-details__value">{row.near_area_name || row.area_name || '\u2014'}</span>
                            </div>
                            <div className="mac-details__row">
                                <span className="mac-details__label">Resident Room</span>
                                <span className="mac-details__value">{row.area_name || '\u2014'}</span>
                            </div>
                            <div className="mac-details__row">
                                <span className="mac-details__label">Device</span>
                                <span className="mac-details__value">{row.device_name || '\u2014'}</span>
                            </div>
                            <div className="mac-details__row">
                                <span className="mac-details__label">Device Number</span>
                                <span className="mac-details__value">{row.manufacture_uuid || '\u2014'}</span>
                            </div>
                            <div className="mac-details__row">
                                <span className="mac-details__label">Device Description</span>
                                <span className="mac-details__value">{row.device_description || '\u2014'}</span>
                            </div>
                            <div className="mac-details__row">
                                <span className="mac-details__label">Event Type</span>
                                <span className="mac-details__value">{row.event_type || '\u2014'}</span>
                            </div>
                            <div className="mac-details__row">
                                <span className="mac-details__label">Alert Time</span>
                                <span className="mac-details__value">{formatUtcToLocal(row.event_datetime_utc)}</span>
                            </div>
                        </>
                        : <>
                            <div className="mac-details__row">
                                <span className="mac-details__label">Device Location</span>
                                <span className="mac-details__value">{row.area_name || '\u2014'}</span>
                            </div>
                            <div className="mac-details__row">
                                <span className="mac-details__label">Device</span>
                                <span className="mac-details__value">{row.device_name || '\u2014'}</span>
                            </div>
                            <div className="mac-details__row">
                                <span className="mac-details__label">Device Number</span>
                                <span className="mac-details__value">{row.manufacture_uuid || '\u2014'}</span>
                            </div>
                            <div className="mac-details__row">
                                <span className="mac-details__label">Device Description</span>
                                <span className="mac-details__value">{row.device_description || '\u2014'}</span>
                            </div>
                            <div className="mac-details__row">
                                <span className="mac-details__label">Event Type</span>
                                <span className="mac-details__value">{row.event_type || '\u2014'}</span>
                            </div>
                            <div className="mac-details__row">
                                <span className="mac-details__label">Alert Time</span>
                                <span className="mac-details__value">{formatUtcToLocal(row.event_datetime_utc)}</span>
                            </div>
                        </>
                }
                </div>
            ) : null}

            <div
                className={`mac-action${holdable ? '' : ' mac-action--static'}`}
                aria-hidden="true"
            >
                <span className="mac-action__fill" style={{ width: `${progressPct}%`, background: accentColor }} />
                <span className="mac-action__label">{footerText}</span>
            </div>

            {(showAcknowledge || showViewVideo) ? (
                <div className="mac-integration-actions">
                    {showAcknowledge ? (
                        <button
                            type="button"
                            className="mac-acknowledge-btn"
                            title="Acknowledge this fall alert"
                            disabled={pending || !onAcknowledge}
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={(e) => { e.stopPropagation(); if (onAcknowledge) { setAcknowledging(true); onAcknowledge(row); } }}
                        >
                            {acknowledging ? 'Acknowledging…' : 'Acknowledge'}
                        </button>
                    ) : null}
                    {showViewVideo ? (
                        <a
                            className="mac-view-video-btn"
                            href={row.acknowledge_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Open SafelyYou to view the video (opens in a new tab)"
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={(e) => { e.stopPropagation(); if (onViewVideo) onViewVideo(row); }}
                        >
                            View Video
                            <i className="fe fe-external-link" aria-hidden="true" />
                        </a>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
};

export default MobileAlertCard;
